import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPostByPath, getStaticPostForPath, formatBlogDate, slugifyBlogTag } from '@/utils/blog';
import { BlogPost as BlogPostType } from '@/types/blog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from 'lucide-react';
import RelatedBlogPosts from '@/components/RelatedBlogPosts';
import ReactMarkdown from 'react-markdown';
import PageMeta from '@/components/seo/PageMeta';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { getSportRouteDetails, trackAnalyticsEvent } from '@/lib/analytics';

type BlogContentFormat = 'html' | 'markdown';

const STATIC_RELOAD_PREFIX = 'skore.blog.staticReload';

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

function getStaticReloadKey(path: string): string {
  return `${STATIC_RELOAD_PREFIX}:${path}`;
}

function clearStaticReloadAttempt(path: string) {
  try {
    window.sessionStorage.removeItem(getStaticReloadKey(path));
  } catch {
    // Ignore storage access errors.
  }
}

function loadStaticBlogDocument(path: string): boolean {
  if (import.meta.env.DEV) {
    return false;
  }

  try {
    const reloadKey = getStaticReloadKey(path);
    if (window.sessionStorage.getItem(reloadKey)) {
      return false;
    }
    window.sessionStorage.setItem(reloadKey, '1');
  } catch {
    return false;
  }

  if (normalizePath(window.location.pathname) === normalizePath(path)) {
    window.location.reload();
  } else {
    window.location.assign(path);
  }

  return true;
}

const BlogPost: React.FC = () => {
  const navigate = useNavigate();
  // Update params to match new URL structure
  const { year, month, day, slug } = useParams<{ 
    year: string;
    month: string;
    day: string;
    slug: string;
  }>();
  
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [contentFormat, setContentFormat] = useState<BlogContentFormat>('markdown');
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();
  const { consentSettings } = useCookieConsent();
  const bodyContentRef = useRef<HTMLDivElement | null>(null);
  
  useEffect(() => {
    const fetchPost = async () => {
      let handedOffToStaticDocument = false;

      if (!year || !month || !day || !slug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const staticUrl = `/blog/${year}/${month}/${day}/${slug}`;
      const staticPost = getStaticPostForPath(year, month, day, slug);

      if (staticPost) {
        console.log('Found matching static blog post data:', staticPost);
        clearStaticReloadAttempt(staticUrl);
        setContentFormat('html');
        setPost(staticPost);
        setLoading(false);
        return;
      }
      
      try {
        // Try to fetch the post content directly
        const fetchedPost = await getPostByPath(year, month, day, slug);
        if (fetchedPost) {
          console.log('Fetched post:', fetchedPost);
          clearStaticReloadAttempt(staticUrl);
          setContentFormat('markdown');
          setPost(fetchedPost);
        } else {
          if (loadStaticBlogDocument(staticUrl)) {
            handedOffToStaticDocument = true;
            return;
          }

          setPost(null);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        toast({
          title: "Failed to load blog post",
          description: "There was an error loading the blog post. Please try again later.",
          variant: "destructive"
        });
      } finally {
        if (!handedOffToStaticDocument) {
          setLoading(false);
        }
      }
    };
    
    fetchPost();
  }, [year, month, day, slug, toast]);
  
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-950">
        <PageMeta
          title={`${slug ? slug.replace(/-/g, ' ') : 'Blog Post'} | skore.info Blog`}
          description="Read spoiler-free sports articles, product notes, and viewing tips on skore.info."
          canonicalPath={year && month && day && slug ? `/blog/${year}/${month}/${day}/${slug}` : '/blog'}
          keywords={['skore.info blog', 'spoiler-free sports', 'sports viewing']}
          ogType="article"
        />
        <Header />
        <main className="flex-1 py-8 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="h-8 w-24 animate-pulse rounded bg-gray-800 mb-4" />
            <div className="h-12 w-full animate-pulse rounded bg-gray-800 mb-8" />
            <div className="aspect-video w-full animate-pulse rounded bg-gray-800 mb-8" />
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-4 w-full animate-pulse rounded bg-gray-800" />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-950">
        <PageMeta
          title="Post Not Found | skore.info Blog"
          description="The blog post you requested could not be found on skore.info."
          canonicalPath={year && month && day && slug ? `/blog/${year}/${month}/${day}/${slug}` : '/blog'}
          keywords={['skore.info blog', 'spoiler-free sports']}
          ogType="article"
        />
        <Header />
        <main className="flex-1 py-8 px-4">
          <div className="container mx-auto max-w-3xl text-center">
            <h1 className="mb-4 text-3xl font-bold text-white">Post Not Found</h1>
            <p className="mb-6 text-gray-400">The blog post you're looking for doesn't exist or has been removed.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Create canonical URL with the date-based format
  const canonicalUrl = `https://skore.info/blog/${year}/${month}/${day}/${slug}`;

  const handleSportRouteClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const routeDetails = getSportRouteDetails(anchor.href);
    if (!routeDetails || !post?.slug) {
      return;
    }

    const linkContext = bodyContentRef.current?.contains(anchor) ? 'body' : 'nav';

    trackAnalyticsEvent('blog_to_sport_click', {
      post_slug: post.slug,
      sport: routeDetails.sport,
      destination_path: routeDetails.destinationPath,
      link_context: linkContext,
      ...routeDetails.utmParams,
    }, consentSettings.analytics);

    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || anchor.target === '_blank'
    ) {
      return;
    }

    event.preventDefault();
    navigate(routeDetails.destinationPath, {
      state: { entrySource: 'blog' },
    });
  };
  
  return (
    <div className="flex min-h-screen flex-col bg-gray-950" onClickCapture={handleSportRouteClick}>
      <PageMeta
        title={`${post.title} | skore.info Blog`}
        description={post.description}
        canonicalPath={canonicalUrl.replace('https://skore.info', '')}
        keywords={post.tags}
        ogType="article"
      />
      <Header />
      
      <main className="flex-1 py-8 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-6">
            <a 
              href="/blog" 
              className="inline-flex items-center text-indigo-400 hover:text-indigo-300"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </a>
          </div>
          
          <h1 className="mb-4 text-3xl md:text-4xl font-bold text-white">{post.title}</h1>
          
          <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-400">
            <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
            <span>{post.readingTime?.text}</span>
          </div>
          
          {post.thumbnail && (
            <div className="mb-8 overflow-hidden rounded-lg bg-gray-900 flex justify-center">
              <img
                src={post.thumbnail}
                alt={post.title}
                className="max-h-[480px] w-auto object-contain"
                loading="lazy"
              />
            </div>
          )}
          
          <div className="mb-8 flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Link
                to={`/blog/tags/${slugifyBlogTag(tag)}`}
                key={tag}
                className="rounded-full bg-indigo-900/50 px-3 py-1 text-xs font-medium text-indigo-200 hover:bg-indigo-700/50 transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
          
          {/* Render the blog content directly from our content property */}
          <div 
            ref={bodyContentRef}
            className="prose prose-invert prose-lg max-w-none prose-headings:mt-6 prose-headings:mb-4 prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:my-4 prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-indigo-500 prose-pre:bg-gray-800 prose-pre:border prose-pre:border-gray-700 prose-img:rounded-lg prose-img:mx-auto prose-img:max-h-[480px] prose-img:object-contain"
          >
            {/* Conditionally render markdown or HTML */}
            {contentFormat === 'markdown' ? (
              <ReactMarkdown>{post.content}</ReactMarkdown>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            )}
          </div>

          {/* Related Blog Posts Section */}
          {post.tags && post.slug && (
            <RelatedBlogPosts currentPostTags={post.tags} currentPostSlug={post.slug} />
          )}

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost;
